// src/components/RouteGenerator.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { claseUrgencia, esUrgente, requiereAtencion } from '../utils/urgencia';
import {
  TECH_MAP_COLORS, buildNotaIAHtml, buildPrintHTML, buildOpsSummaryHTML, buildGoogleMapsUrl,
} from '../utils/routePrintHtml';
import QRCode from 'qrcode';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ── Constantes ────────────────────────────────────────────────────────────────
const TECH_COLORS = {
  Benito: { badge: 'bg-blue-100 text-blue-800 border-blue-200',   bar: 'bg-blue-500'   },
  Pablo:  { badge: 'bg-green-100 text-green-800 border-green-200', bar: 'bg-green-500'  },
  Dani:   { badge: 'bg-amber-100 text-amber-800 border-amber-200', bar: 'bg-amber-500'  },
  Ivan:   { badge: 'bg-red-100 text-red-800 border-red-200',       bar: 'bg-red-500'    },
};

const TIPO_STYLES = {
  'Pendiente':  'bg-gray-100 text-gray-700',
  'Ausente':    'bg-purple-100 text-purple-700',
  'Gestionada': 'bg-green-100 text-green-700',
  'Asignada':   'bg-blue-100 text-blue-700',
};

// Niveles de prioridad del backlog (generate-routes.js, calcularNivel()) —
// 1 más urgente (rojo) a 5 menos (gris), para "Consultar cola".
const NIVEL_BADGE = {
  1: 'bg-red-100 text-red-700',
  2: 'bg-orange-100 text-orange-700',
  3: 'bg-amber-100 text-amber-700',
  4: 'bg-blue-100 text-blue-700',
  5: 'bg-gray-100 text-gray-600',
};

const OFFICE = [37.86670818025596, -0.7939694516834689];

// Calendario y apoyo (espejo del backend, para filtrar el pool en el cliente)
const CLIENT_CALENDARIO = {
  Lunes:     { Benito:['Zona 5'],  Ivan:['Zona 4'], Pablo:['Zona 1-2'], Dani:['Zona 3'] },
  Martes:    { Benito:['Zona 6'],  Ivan:['Zona 4'], Pablo:['Zona 1-2'], Dani:['Zona 3'] },
  Miercoles: { Benito:['Zona 5'],  Ivan:['Zona 4'], Pablo:['Zona 1-2'], Dani:['Zona 3'] },
  Jueves:    { Benito:['Zona 5'],  Ivan:['Zona 4'], Pablo:['Zona 1-2'], Dani:['Zona 3'] },
  Viernes:   { Benito:['Zona 6'],  Ivan:['Zona 4'], Pablo:['Zona 1-2'], Dani:['Zona 3'] },
};
// Apoyo estático — Benito solo cuando benitoZonasActivas lo permita (dinámico)
const CLIENT_APOYO_STATIC = {
  'Zona 5':   ['Pablo', 'Benito'],
  'Zona 6':   ['Ivan',  'Benito'],
  'Zona 3':   ['Ivan',  'Pablo'],
  'Zona 1-2': ['Dani',  'Benito'],
  'Zona 4':   ['Dani'],
};

function getClientApoyo(benitoZonasActivas = []) {
  const apoyo = { ...CLIENT_APOYO_STATIC };
  for (const z of benitoZonasActivas) {
    if (!apoyo[z]) apoyo[z] = [];
    if (!apoyo[z].includes('Benito')) apoyo[z] = [...apoyo[z], 'Benito'];
  }
  return apoyo;
}

function tecnicoMatchesZona(tecnico, zona, dia, benitoZonasActivas = []) {
  if (!zona || !dia) return false;
  const dayMap = CLIENT_CALENDARIO[dia];
  if (!dayMap) return false;
  if ((dayMap[tecnico] || []).includes(zona)) return true;
  const apoyo = getClientApoyo(benitoZonasActivas);
  if ((apoyo[zona] || []).includes(tecnico)) return true;
  return false;
}

function getTitular(zona, dia) {
  if (!zona || !dia) return null;
  const dayMap = CLIENT_CALENDARIO[dia];
  if (!dayMap) return null;
  return Object.keys(dayMap).find(t => (dayMap[t] || []).includes(zona)) || null;
}

// ── Helpers de horario ────────────────────────────────────────────────────────
function rowBg(order) {
  if (order.yaAsignada) return 'bg-blue-50/40 border-l-2 border-l-blue-300';
  if (order.prioridad === 1) return 'bg-red-50/40 border-l-2 border-l-red-300';
  if (order.prioridad === 2) return 'bg-green-50/30 border-l-2 border-l-green-300';
  return '';
}

function nextWorkingDay() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

function fmtEspera(dias) {
  if (dias === null || dias === undefined) return '';
  if (dias === 0) return 'Hoy';
  return `${dias}d`;
}
const esperaColor = claseUrgencia; // fuente única: utils/urgencia (7 atención / 30 urgente)

const START_MIN  = 9 * 60 + 30; // 9:30
const TRAVEL_MIN = 20;
const LUNCH_START = 14 * 60;
const LUNCH_END   = 15 * 60;
const MAX_ORDENES_DIA_CLIENT = 6;
const MAX_MINUTOS_DIA_CLIENT = 450;

function fmtMin(t) {
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}
function rebuildSchedule(orders) {
  let min = START_MIN;
  return orders.map((item, i) => {
    if (min >= LUNCH_START && min < LUNCH_END) min = LUNCH_END;
    const dur = item.duracion || 65;
    const hi  = fmtMin(min);
    const hf  = fmtMin(min + dur);
    min += dur + TRAVEL_MIN;
    return { ...item, horaInicio: hi, horaFin: hf, orden: i + 1 };
  });
}
function calcHoraFin(orders) {
  if (!orders.length) return '09:30';
  let min = START_MIN;
  orders.forEach(o => {
    if (min >= LUNCH_START && min < LUNCH_END) min = LUNCH_END;
    min += (o.duracion || 65) + TRAVEL_MIN;
  });
  return fmtMin(min - TRAVEL_MIN);
}
function buildRouteMeta(rt, orders) {
  return {
    ...rt,
    orders,
    totalOrdenes:      orders.length,
    nuevasOrdenes:     orders.filter(o => !o.yaAsignada).length,
    existentesOrdenes: orders.filter(o =>  o.yaAsignada).length,
    horaFinEstimada:   calcHoraFin(orders),
  };
}

// ── Mapa ──────────────────────────────────────────────────────────────────────
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) map.fitBounds(points, { padding: [32, 32], maxZoom: 13 });
  }, [map, points]);
  return null;
}

function RouteMap({ routes, generadoEn, geocodedCoords = {} }) {
  const allMarkers = Object.values(routes).flatMap(rt =>
    rt.orders.map(o => {
      const lat = o.lat || geocodedCoords[o.id]?.lat;
      const lng = o.lng || geocodedCoords[o.id]?.lng;
      if (!lat || !lng) return null;
      return { ...o, lat, lng, tecnico: rt.tecnico };
    }).filter(Boolean)
  );
  const bounds = [OFFICE, ...allMarkers.map(o => [o.lat, o.lng])];
  return (
    <MapContainer key={generadoEn} center={OFFICE} zoom={10}
      style={{ height:'420px', width:'100%' }} scrollWheelZoom>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
      <FitBounds points={bounds}/>
      <CircleMarker center={OFFICE} radius={11}
        pathOptions={{ color:'#fff', fillColor:'#dc2626', fillOpacity:1, weight:2 }}>
        <Popup><strong>Oficina</strong><br/>Climatización Zapata</Popup>
      </CircleMarker>
      {allMarkers.map(order => {
        const color = TECH_MAP_COLORS[order.tecnico] || '#6b7280';
        return (
          <CircleMarker key={`${order.id}-${order.tecnico}`}
            center={[order.lat, order.lng]}
            radius={order.yaAsignada ? 7 : 9}
            pathOptions={{ color:'#fff', fillColor:color, fillOpacity:order.yaAsignada?0.55:0.88, weight:1.5 }}>
            <Popup>
              <div style={{ minWidth:160 }}>
                <strong>{order.orden}. {order.customerName}</strong><br/>
                <span style={{ color:'#6b7280', fontSize:11 }}>{order.reference}</span><br/>
                {order.address && <><span style={{ fontSize:11 }}>{order.address}</span><br/></>}
                <span style={{ color, fontWeight:600 }}>{order.tecnico}</span>{' · '}{order.tipoLabel}
                {order.diasEspera!=null&&!order.yaAsignada&&(
                  <><br/><span style={{ color:'#9ca3af', fontSize:11 }}>{order.diasEspera}d espera</span></>
                )}
              </div>
            </Popup>
          </CircleMarker>
        );
      })}
    </MapContainer>
  );
}

// ── Fila arrastrable ──────────────────────────────────────────────────────────
function SortableOrderRow({ order, tecnico, otherTechnicians, onRemove, onMove, isMenuOpen, onToggleMenu }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: order.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity:  isDragging ? 0.35 : 1,
    zIndex:   isDragging ? 20 : 'auto',
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style} className={`flex items-start gap-2 px-4 py-3 ${rowBg(order)}`}>
      {/* Drag handle */}
      <button {...attributes} {...listeners} tabIndex={-1}
        className="flex-shrink-0 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing mt-1 touch-none select-none">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <circle cx="7" cy="4"  r="1.5"/><circle cx="13" cy="4"  r="1.5"/>
          <circle cx="7" cy="10" r="1.5"/><circle cx="13" cy="10" r="1.5"/>
          <circle cx="7" cy="16" r="1.5"/><circle cx="13" cy="16" r="1.5"/>
        </svg>
      </button>
      {/* Nº */}
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs font-bold flex items-center justify-center mt-0.5">
        {order.orden}
      </div>
      {/* Hora */}
      <div className="flex-shrink-0 text-xs font-mono text-gray-500 mt-0.5 w-24">
        {order.horaInicio}<br/>
        <span className="text-gray-400">↓ {order.duracion}min</span>
      </div>
      {/* Detalle */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 font-mono">{order.reference}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${TIPO_STYLES[order.tipoLabel]||'bg-gray-100 text-gray-600'}`}>
            {order.tipoLabel}
          </span>
          {order.zona    && <span className="text-xs text-gray-400">· {order.zona}</span>}
          {order.esApoyo && <span className="text-xs text-amber-600 font-medium">apoyo</span>}
        </div>
        <div className="font-medium text-sm text-gray-800 mt-0.5 truncate">{order.customerName}</div>
        {order.address && <div className="text-xs text-gray-500 truncate mt-0.5">{order.address}</div>}
      </div>
      {/* Espera + CP */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1 min-w-[52px]">
        {!order.yaAsignada && order.diasEspera !== null && (
          <span className={`text-xs font-medium ${esperaColor(order.diasEspera)}`}>
            {fmtEspera(order.diasEspera)}
          </span>
        )}
        {order.cp && (
          <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">{order.cp}</span>
        )}
      </div>
      {/* Menú acciones */}
      <div className="relative flex-shrink-0 self-center">
        <button onClick={() => onToggleMenu(order.id)}
          className={`p-1 rounded-md transition-colors ${isMenuOpen?'bg-gray-100 text-gray-700':'text-gray-300 hover:text-gray-600 hover:bg-gray-100'}`}
          title="Acciones">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <circle cx="10" cy="4"  r="1.5"/>
            <circle cx="10" cy="10" r="1.5"/>
            <circle cx="10" cy="16" r="1.5"/>
          </svg>
        </button>
        {isMenuOpen && (
          <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden min-w-[170px]">
            <button onClick={() => onRemove(tecnico, order.id)}
              className="w-full text-left px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-b border-gray-100">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
              Quitar de la ruta
            </button>
            {otherTechnicians.length > 0 && (
              <div>
                <div className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Mover a</div>
                {otherTechnicians.map(t => (
                  <button key={t} onClick={() => onMove(tecnico, t, order.id)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: TECH_MAP_COLORS[t]||'#6b7280' }}/>
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tarjeta de técnico ────────────────────────────────────────────────────────
function TechRouteCard({ rt, isOpen, onToggle, onReorder, allTechnicians, onRemove, onMove,
  activeMenuId, onToggleMenu, poolNext, onAddFromPool }) {

  const tc = TECH_COLORS[rt.tecnico] || { badge:'bg-gray-100 text-gray-700 border-gray-200', bar:'bg-gray-500' };
  const otherTechnicians = allTechnicians.filter(t => t !== rt.tecnico);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = rt.orders.findIndex(o => o.id === active.id);
    const newIdx = rt.orders.findIndex(o => o.id === over.id);
    if (oldIdx !== -1 && newIdx !== -1) onReorder(rt.tecnico, arrayMove(rt.orders, oldIdx, newIdx));
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Cabecera */}
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
        <div className="flex items-center gap-3">
          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border ${tc.badge}`}>
            {rt.tecnico.charAt(0)}
          </span>
          <div className="text-left">
            <div className="font-bold text-gray-800">{rt.tecnico}</div>
            <div className="text-xs text-gray-500">
              {rt.totalOrdenes} paradas · {rt.nuevasOrdenes} nuevas · {rt.existentesOrdenes} ya asignadas · fin {rt.horaFinEstimada}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1.5">
            <div className="w-20 bg-gray-200 rounded-full h-1.5">
              <div className={`h-1.5 rounded-full ${tc.bar}`} style={{ width:`${Math.min(100,rt.totalOrdenes/6*100)}%` }}/>
            </div>
            <span className="text-xs text-gray-500">{rt.totalOrdenes}/6</span>
          </div>
          <svg className={`w-4 h-4 text-gray-500 transition-transform ${isOpen?'rotate-180':''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
          </svg>
        </div>
      </button>

      {/* Lista de órdenes */}
      {isOpen && (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={rt.orders.map(o => o.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y divide-gray-100">
                {rt.orders.map(order => (
                  <SortableOrderRow
                    key={order.id}
                    order={order}
                    tecnico={rt.tecnico}
                    otherTechnicians={otherTechnicians}
                    onRemove={onRemove}
                    onMove={onMove}
                    isMenuOpen={activeMenuId === order.id}
                    onToggleMenu={onToggleMenu}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Botón agregar siguiente orden del pool */}
          {poolNext ? (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => onAddFromPool(rt.tecnico)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-dashed border-gray-300 hover:border-green-400 hover:bg-green-50 transition-colors group text-left"
              >
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-green-100 flex items-center justify-center transition-colors">
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-500 group-hover:text-green-700 transition-colors">
                    Agregar siguiente orden por prioridad
                  </div>
                  <div className="text-xs text-gray-400 truncate mt-0.5">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${poolNext.prioridad===1?'bg-red-400':'bg-green-400'}`}/>
                    {poolNext.reference} · {poolNext.customerName}
                    {poolNext.diasEspera!=null && ` · ${poolNext.diasEspera}d espera`}
                  </div>
                </div>
                <span className="flex-shrink-0 text-xs text-gray-400 group-hover:text-green-600 font-mono transition-colors">
                  {poolNext.zona}
                </span>
              </button>
            </div>
          ) : rt.orders.length > 0 ? (
            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-xs text-gray-400 text-center">
              Sin más órdenes disponibles para esta zona
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ── Caché de geocodificación (sessionStorage) ─────────────────────────────────
const GEO_CACHE_KEY = 'zapata_geo_cache';
const geoSessionCache = (() => {
  try { return JSON.parse(sessionStorage.getItem(GEO_CACHE_KEY) || '{}'); }
  catch { return {}; }
})();
const saveGeoEntry = (key, coords) => {
  try {
    geoSessionCache[key] = coords;
    sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(geoSessionCache));
  } catch {}
};

// ── Componente principal ──────────────────────────────────────────────────────
const RouteGenerator = () => {
  const [fecha, setFecha]         = useState(nextWorkingDay);
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState(null);
  const [open, setOpen]           = useState(true);
  const [kpiCierre, setKpiCierre] = useState(null); // { media, total } | null
  const [expanded, setExpanded]   = useState({});
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [showExcluidas, setShowExcluidas] = useState(false);
  const [showMap, setShowMap]         = useState(false);
  const [benitoZonasActivas, setBenitoZonasActivas] = useState([]);
  const [confirming, setConfirming]   = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [capacityWarning, setCapacityWarning] = useState('');
  const [geocodedCoords, setGeocodedCoords] = useState({});
  const [geocodingProg, setGeocodingProg]   = useState({ total: 0, done: 0 });
  const geocodingAbort = useRef(false);
  const [printMenu, setPrintMenu] = useState(false);
  const [generandoNotas, setGenerandoNotas] = useState(false);
  const [notasProgress, setNotasProgress] = useState({ total: 0, done: 0 });
  const [routeOrders, setRouteOrders]     = useState({});
  const [removedOrders, setRemovedOrders] = useState([]);
  const [activeMenuId, setActiveMenuId]   = useState(null);
  // Pool: órdenes con zona válida que no cupieron en el reparto inicial
  const [availablePool, setAvailablePool] = useState([]);
  // Búsqueda manual de orden para técnico
  const [showOrderSearch, setShowOrderSearch] = useState(false);
  const [searchTech, setSearchTech]           = useState('');
  const [poolLoading, setPoolLoading]         = useState(false);
  const [diaNombre, setDiaNombre]             = useState('');
  // "Consultar cola" (pedido del usuario 2026-10-08): la cola real del
  // backlog tal como la usa el motor para asignar, con el nivel y el
  // motivo de cada una — basada en `cola` de generate-routes.js, no en un
  // cálculo aparte en el cliente.
  const [showCola, setShowCola] = useState(false);
  const [cola, setCola]         = useState([]);
  const [colaLoading, setColaLoading] = useState(false);
  const [colaPage, setColaPage]           = useState(1);
  const [colaTotalPages, setColaTotalPages] = useState(1);
  const [colaTotal, setColaTotal]         = useState(0);
  const [colaZombies, setColaZombies]     = useState({ total: 0, items: [] });
  const [showZombies, setShowZombies]     = useState(false);
  const COLA_PAGE_SIZE = 50;

  // Inicializar al recibir resultado
  useEffect(() => {
    if (!result) {
      setRouteOrders({}); setRemovedOrders([]); setAvailablePool([]);
      setDiaNombre(''); setBenitoZonasActivas([]); setConfirmResult(null);
      return;
    }
    const init = {};
    Object.entries(result.routes).forEach(([n, rt]) => { init[n] = { ...rt }; });
    setRouteOrders(init);
    setRemovedOrders([]);
    setDiaNombre(result.diaNombre || '');
    setBenitoZonasActivas(result.benitoZonasActivas || []);
    setConfirmResult(null);
    const pool = [...(result.sinDisponibilidad || [])].sort(
      (a, b) => a.prioridad - b.prioridad || (b.diasEspera||0) - (a.diasEspera||0)
    );
    setAvailablePool(pool);
  }, [result]);

  // KPI tiempo de cierre — se carga una sola vez al montar
  useEffect(() => {
    fetch('/.netlify/functions/getKpiCierreMedio', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => { if (d.success) setKpiCierre({ media: d.media, total: d.total }); })
      .catch(() => {});
  }, []);

  // Resetear geocodificación cuando llegan nuevas rutas
  useEffect(() => {
    setGeocodedCoords({});
    setGeocodingProg({ total: 0, done: 0 });
    geocodingAbort.current = true; // Abortar cualquier geocodificación en curso
  }, [result]);

  // Geocodificar órdenes sin coordenadas al abrir el mapa
  useEffect(() => {
    if (!showMap || !routeOrders) return;

    const sinCoords = Object.values(routeOrders)
      .flatMap(rt => rt.orders)
      .filter(o => (!o.lat || !o.lng));

    // Separar las que ya están en caché de las que hay que pedir a Nominatim
    const nuevas = [];
    const fromCache = {};
    for (const o of sinCoords) {
      const key = `${o.address}|${o.cp}`;
      if (geoSessionCache[key]) {
        fromCache[o.id] = geoSessionCache[key];
      } else {
        nuevas.push(o);
      }
    }

    if (Object.keys(fromCache).length) {
      setGeocodedCoords(prev => ({ ...prev, ...fromCache }));
    }
    if (!nuevas.length) return;

    setGeocodingProg({ total: nuevas.length, done: 0 });
    geocodingAbort.current = false;

    const run = async () => {
      let done = 0;
      for (const o of nuevas) {
        if (geocodingAbort.current) break;
        try {
          const q = [o.address, o.cp, 'España'].filter(Boolean).join(', ');
          if (!q.trim()) continue;
          const res  = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1&countrycodes=es`,
            { headers: { 'User-Agent': 'ClimatizacionZapata/1.0 (contacto@climatizacionzapata.es)' } }
          );
          const data = await res.json();
          if (data.length) {
            const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
            saveGeoEntry(`${o.address}|${o.cp}`, coords);
            setGeocodedCoords(prev => ({ ...prev, [o.id]: coords }));
          }
        } catch { /* sin coordenadas para esta orden */ }
        done++;
        setGeocodingProg({ total: nuevas.length, done });
        if (done < nuevas.length) await new Promise(r => setTimeout(r, 1100)); // Nominatim: 1 req/s
      }
    };
    run();
    return () => { geocodingAbort.current = true; };
  }, [showMap, routeOrders]); // eslint-disable-line

  // Cerrar menú de acciones al hacer scroll
  useEffect(() => {
    if (!activeMenuId) return;
    const close = () => setActiveMenuId(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [activeMenuId]);

  // ── Callbacks de edición de rutas ─────────────────────────────────────────
  const handleReorder = useCallback((tecnico, newOrders) => {
    const rebuilt = rebuildSchedule(newOrders);
    setRouteOrders(prev => ({ ...prev, [tecnico]: buildRouteMeta(prev[tecnico], rebuilt) }));
  }, []);

  const handleRemoveOrder = useCallback((tecnico, orderId) => {
    setActiveMenuId(null);
    setRouteOrders(prev => {
      const rt = prev[tecnico];
      if (!rt) return prev;
      const order = rt.orders.find(o => o.id === orderId);
      if (!order) return prev;
      const newOrders = rebuildSchedule(rt.orders.filter(o => o.id !== orderId));
      setRemovedOrders(r => [...r, { ...order, tecnicoOrigen: tecnico }]);
      return { ...prev, [tecnico]: buildRouteMeta(rt, newOrders) };
    });
  }, []);

  const handleMoveOrder = useCallback((fromTecnico, toTecnico, orderId) => {
    setActiveMenuId(null);
    const toRt  = routeOrders[toTecnico];
    const order = routeOrders[fromTecnico]?.orders.find(o => o.id === orderId);
    if (toRt && order) {
      const toMinutos = toRt.orders.reduce((s, o) => s + (o.duracion || 65), 0);
      if (toRt.orders.length >= MAX_ORDENES_DIA_CLIENT || toMinutos + (order.duracion || 65) > MAX_MINUTOS_DIA_CLIENT) {
        setCapacityWarning(
          `${toTecnico} ya tiene ${toRt.orders.length} órdenes · ${toMinutos}min — capacidad máxima superada`
        );
        setTimeout(() => setCapacityWarning(''), 4000);
        return;
      }
    }
    setCapacityWarning('');
    setRouteOrders(prev => {
      const fromRt = prev[fromTecnico];
      const toRt2  = prev[toTecnico];
      if (!fromRt || !toRt2) return prev;
      const ord = fromRt.orders.find(o => o.id === orderId);
      if (!ord) return prev;
      const newFrom = rebuildSchedule(fromRt.orders.filter(o => o.id !== orderId));
      const newTo   = rebuildSchedule([...toRt2.orders, ord]);
      return {
        ...prev,
        [fromTecnico]: buildRouteMeta(fromRt, newFrom),
        [toTecnico]:   buildRouteMeta(toRt2,  newTo),
      };
    });
  }, [routeOrders]);

  const handleRestoreOrder = useCallback((removed) => {
    const { tecnicoOrigen, ...order } = removed;
    setRemovedOrders(prev => prev.filter(o => o.id !== removed.id));
    setRouteOrders(prev => {
      if (!prev[tecnicoOrigen]) return prev;
      const rt = prev[tecnicoOrigen];
      const newOrders = rebuildSchedule([...rt.orders, order]);
      return { ...prev, [tecnicoOrigen]: buildRouteMeta(rt, newOrders) };
    });
  }, []);

  const handleToggleMenu = useCallback((orderId) => {
    setActiveMenuId(prev => (prev === orderId ? null : orderId));
  }, []);

  // Asignar orden específica del pool a un técnico (búsqueda manual)
  const handleAssignFromSearch = useCallback((tecnico, orderId) => {
    const order = availablePool.find(o => o.id === orderId);
    if (!order) return;
    const rt = routeOrders[tecnico];
    if (rt) {
      const toMin = rt.orders.reduce((s, o) => s + (o.duracion || 65), 0);
      if (rt.orders.length >= MAX_ORDENES_DIA_CLIENT || toMin + (order.duracion || 65) > MAX_MINUTOS_DIA_CLIENT) {
        setCapacityWarning(`${tecnico} ya tiene ${rt.orders.length} órdenes · ${toMin}min — capacidad máxima superada`);
        setTimeout(() => setCapacityWarning(''), 4000);
        return;
      }
    }
    setCapacityWarning('');
    setAvailablePool(prev => prev.filter(o => o.id !== orderId));
    setRouteOrders(prev => {
      const r = prev[tecnico] || { tecnico, orders: [], totalOrdenes: 0, nuevasOrdenes: 0, existentesOrdenes: 0, horaFinEstimada: '09:30', osrmUsed: false };
      const newOrders = rebuildSchedule([...r.orders, order]);
      return { ...prev, [tecnico]: buildRouteMeta(r, newOrders) };
    });
  }, [availablePool, routeOrders]);

  // Agregar la siguiente orden del pool que encaje con la zona del técnico
  const handleAddFromPool = useCallback((tecnico) => {
    const dia = result?.diaNombre;
    const next = availablePool.find(o => tecnicoMatchesZona(tecnico, o.zona, dia, benitoZonasActivas));
    if (!next) return;
    const rt = routeOrders[tecnico];
    if (rt) {
      const toMin = rt.orders.reduce((s, o) => s + (o.duracion || 65), 0);
      if (rt.orders.length >= MAX_ORDENES_DIA_CLIENT || toMin + (next.duracion || 65) > MAX_MINUTOS_DIA_CLIENT) {
        setCapacityWarning(`${tecnico} ya tiene ${rt.orders.length} órdenes · ${toMin}min — capacidad máxima superada`);
        setTimeout(() => setCapacityWarning(''), 4000);
        return;
      }
    }
    setCapacityWarning('');
    setAvailablePool(prev => prev.filter(o => o.id !== next.id));
    setRouteOrders(prev => {
      const r = prev[tecnico];
      if (!r) return prev;
      const newOrders = rebuildSchedule([...r.orders, next]);
      return { ...prev, [tecnico]: buildRouteMeta(r, newOrders) };
    });
  }, [availablePool, result, routeOrders]);

  // Cargar solo el pool (sin mostrar la propuesta completa)
  const handleLoadPool = useCallback(async () => {
    if (result) { setShowOrderSearch(true); return; }
    setPoolLoading(true); setError(null);
    try {
      const res  = await fetch(`/.netlify/functions/generate-routes?fecha=${fecha}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al cargar órdenes');
      const pool = [...(data.sinDisponibilidad || [])].sort(
        (a, b) => a.prioridad - b.prioridad || (b.diasEspera||0) - (a.diasEspera||0)
      );
      setAvailablePool(pool);
      setDiaNombre(data.diaNombre || '');
      setShowOrderSearch(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setPoolLoading(false);
    }
  }, [fecha, result]);

  // Cargar la cola COMPLETA y paginada del backlog (corrección del usuario
  // 2026-10-08: la cola de generate-routes.js solo mira las últimas 500
  // órdenes por fecha de StelOrder, no es la cola completa real) — se pide
  // aparte a getColaBacklog.js, que lee siempre de Firebase, nunca de
  // StelOrder en vivo (mismo criterio que Analítica, para no gastar su API
  // en una consulta de solo revisión).
  const handleLoadCola = useCallback(async (page = 1) => {
    setColaLoading(true); setError(null);
    try {
      const res  = await fetch(`/.netlify/functions/getColaBacklog?page=${page}&pageSize=${COLA_PAGE_SIZE}`, {
        headers: { ...authService.authHeader() },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al cargar la cola');
      setCola(data.cola || []);
      setColaPage(data.page || 1);
      setColaTotalPages(data.totalPages || 1);
      setColaTotal(data.total || 0);
      setColaZombies(data.zombies || { total: 0, items: [] });
      setShowCola(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setColaLoading(false);
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!window.confirm('¿Confirmar y asignar todas las rutas en StelOrder? Esta acción no se puede deshacer.')) return;
    setConfirming(true); setConfirmResult(null);
    try {
      const routes = {};
      Object.entries(routeOrders).forEach(([nombre, rt]) => {
        routes[nombre] = { orders: rt.orders };
      });
      const res  = await fetch('/.netlify/functions/confirm-routes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ fecha, routes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al confirmar rutas');
      setConfirmResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirming(false);
    }
  }, [fecha, routeOrders]);

  const handleGenerate = useCallback(async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const res  = await fetch(`/.netlify/functions/generate-routes?fecha=${fecha}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al generar rutas');
      setResult(data);
      const exp = {};
      Object.keys(data.routes).forEach(k => { exp[k] = true; });
      setExpanded(exp);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fecha]);

  // Ficha del cliente de una orden — da los activos para el aviso de
  // garantías a punto de caducar en la hoja resumen de operaciones.
  //
  // La llamada a generarNotaRutaIA.js (notas IA por orden) se desactivó
  // aquí a propósito (decisión 2026-10-07): no estaban aportando suficiente
  // valor frente al coste/latencia de generarlas. El backend
  // (generarNotaRutaIA.js) y el renderizado (buildNotaIAHtml,
  // TechnicianOrderDetailView.js) se dejan intactos por si se retoma más
  // adelante — ver también OportunidadesTecnicoCard en DashboardView.js,
  // que deja de recibir datos nuevos a partir de aquí porque dependía del
  // campo oportunidadComercial que solo rellenaba esta llamada.
  const generarNotaParaOrden = async (order) => {
    if (!order.clienteId) return { nota: null, activos: [] };
    try {
      const params = new URLSearchParams({ clienteId: order.clienteId, ordenId: order.id });
      const fichaResp = await fetch(`/.netlify/functions/getFichaCliente?${params}`, { headers: { ...authService.authHeader() } });
      const ficha = await fichaResp.json();
      if (!ficha.success) return { nota: null, activos: [] };
      return { nota: null, activos: ficha.activos || [] };
    } catch {
      return { nota: null, activos: [] };
    }
  };

  const generarNotasIA = async (ordersFlat) => {
    setGenerandoNotas(true);
    setNotasProgress({ total: ordersFlat.length, done: 0 });
    const notas = {};
    const activosPorOrden = {};
    await Promise.all(ordersFlat.map(async (order) => {
      const { nota, activos } = await generarNotaParaOrden(order);
      if (nota) notas[order.id] = nota;
      if (activos.length) activosPorOrden[order.id] = activos;
      setNotasProgress(prev => ({ ...prev, done: prev.done + 1 }));
    }));
    setGenerandoNotas(false);
    return { notas, activosPorOrden };
  };

  const doPrint = async (tecnico = null) => {
    if (!result) return;
    // Abrir la pestaña YA, de forma síncrona dentro del clic — si se abre
    // después de los await de abajo, el navegador lo trata como pop-up no
    // solicitado por el usuario y lo bloquea.
    const win = window.open('', '_blank', 'width=1100,height=750');
    win.document.write('<p style="font-family:sans-serif;padding:40px;color:#6b7280">Preparando impresión…</p>');

    const routesToPrint = tecnico
      ? Object.fromEntries(Object.entries(routeOrders).filter(([k]) => k === tecnico))
      : routeOrders;
    const ordersFlat = Object.values(routesToPrint).flatMap(rt => rt.orders);
    const { notas: notasPorOrden } = await generarNotasIA(ordersFlat);

    // QR con la ruta completa del técnico en Google Maps (pedido del usuario
    // 2026-10-08) — se genera aquí (async) y se pasa ya resuelto a
    // buildPrintHTML (síncrona). Si un técnico no tiene paradas con
    // coordenadas, simplemente no lleva QR — no bloquea la impresión.
    const qrPorTecnico = {};
    await Promise.all(Object.entries(routesToPrint).map(async ([nombre, rt]) => {
      const url = buildGoogleMapsUrl(rt.orders, OFFICE[0], OFFICE[1]);
      if (!url) return;
      try {
        const dataUrl = await QRCode.toDataURL(url, { width: 180, margin: 1, errorCorrectionLevel: 'M' });
        qrPorTecnico[nombre] = { dataUrl, url };
      } catch { /* sin QR no bloquea la impresión */ }
    }));

    win.document.open();
    win.document.write(buildPrintHTML({ ...result, routes: routeOrders }, tecnico, notasPorOrden, qrPorTecnico));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);

    // Registro de lo entregado ese día — no bloquea la impresión si falla.
    Object.entries(routesToPrint).forEach(([nombre, rt]) => {
      fetch('/.netlify/functions/registrarEntregaDiaria', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ fecha, tecnico: nombre, ordenIds: rt.orders.map(o => o.id) }),
      }).catch(() => {});
    });
  };

  // Hoja resumen de operaciones (Parte 1 del plan de notas IA): carga por
  // técnico, qué quedó fuera de la propuesta y por qué, avisos de material
  // sin stock/fuera de catálogo (de las notas IA ya generadas) y garantías
  // de equipos a punto de caducar. A diferencia de la hoja por técnico, esta
  // siempre mira TODAS las rutas, no se filtra por técnico.
  const doPrintResumenOperativo = async () => {
    if (!result) return;
    const win = window.open('', '_blank', 'width=1000,height=750');
    win.document.write('<p style="font-family:sans-serif;padding:40px;color:#6b7280">Generando resumen…</p>');

    const ordersFlat = Object.values(routeOrders).flatMap(rt => rt.orders);
    const { notas, activosPorOrden } = await generarNotasIA(ordersFlat);

    win.document.open();
    win.document.write(buildOpsSummaryHTML(result, routeOrders, notas, activosPorOrden));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);
  };

  const allTechnicians  = Object.keys(routeOrders).length > 0
    ? Object.keys(routeOrders)
    : Object.keys(TECH_MAP_COLORS);
  const hasRoutes       = Object.keys(routeOrders).length > 0;
  const dia             = diaNombre;
  const sinZonaCount    = result?.sinZona?.length || 0;
  const sinDisponibCount = result?.sinDisponibilidad?.length || 0;
  const unassignedTotal = sinZonaCount + sinDisponibCount;
  const excluidasPorVentanaCount = result?.excluidasPorVentana?.length || 0;

  return (
    <div className="bg-white rounded-2xl shadow-lg border overflow-hidden">

      {/* Backdrop para cerrar menú de acciones */}
      {activeMenuId && <div className="fixed inset-0 z-20" onClick={() => setActiveMenuId(null)}/>}

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 hover:bg-gray-50 transition-colors">

        {/* Título + badge — zona clicable para expandir */}
        <button onClick={() => setOpen(v => !v)} className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-gray-800">Generador de Rutas</h3>
          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
            Propuesta automática
          </span>
        </button>

        {/* Derecha: KPI + chevron */}
        <div className="flex items-center gap-3">

          {/* KPI tiempo medio de cierre */}
          {kpiCierre?.media !== null && kpiCierre?.media !== undefined && (
            <div className="hidden sm:flex flex-col items-end">
              <div className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-1.5">
                <span className="text-xs text-indigo-400 font-medium leading-none">⌀ cierre</span>
                <span className="text-base font-bold text-indigo-700 leading-none">
                  {kpiCierre.media % 1 === 0 ? kpiCierre.media : kpiCierre.media.toFixed(1)}d
                </span>
              </div>
              <span className="text-xs text-gray-400 mt-0.5 pr-0.5">
                {kpiCierre.total} órdenes · 30d
              </span>
            </div>
          )}
          {kpiCierre?.media === null && kpiCierre !== null && (
            <span className="hidden sm:block text-xs text-gray-400">Sin datos de cierre</span>
          )}

          {/* Chevron */}
          <button onClick={() => setOpen(v => !v)}>
            <svg className={`w-5 h-5 text-gray-500 transition-transform ${open?'rotate-180':''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-5 md:px-6 space-y-4">

          {/* Controles */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Fecha</label>
              <input type="date" value={fecha}
                onChange={e => { setFecha(e.target.value); setResult(null); setAvailablePool([]); setDiaNombre(''); setShowOrderSearch(false); setShowCola(false); setCola([]); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"/>
            </div>
            <button onClick={handleGenerate} disabled={loading || poolLoading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Consultando StelOrder...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                  </svg>
                  Generar propuesta
                </>
              )}
            </button>

            {/* Buscar orden — siempre visible */}
            <button
              onClick={() => { if (showOrderSearch) { setShowOrderSearch(false); } else { setSearchTech(''); handleLoadPool(); } }}
              disabled={loading || poolLoading}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                showOrderSearch
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'border-amber-400 text-amber-700 hover:bg-amber-50'
              }`}
            >
              {poolLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Cargando órdenes...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"/>
                  </svg>
                  Buscar orden
                  {availablePool.length > 0 && (
                    <span className={`rounded-full px-1.5 py-0.5 text-xs font-bold leading-none ${
                      showOrderSearch ? 'bg-white/30 text-white' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {availablePool.length}
                    </span>
                  )}
                </>
              )}
            </button>

            {/* Consultar cola — siempre visible */}
            <button
              onClick={() => { if (showCola) { setShowCola(false); } else { handleLoadCola(); } }}
              disabled={loading || colaLoading}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                showCola
                  ? 'bg-indigo-500 border-indigo-500 text-white'
                  : 'border-indigo-400 text-indigo-700 hover:bg-indigo-50'
              }`}
            >
              {colaLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Cargando cola...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7"/>
                  </svg>
                  Consultar cola
                  {cola.length > 0 && (
                    <span className={`rounded-full px-1.5 py-0.5 text-xs font-bold leading-none ${
                      showCola ? 'bg-white/30 text-white' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      {cola.length}
                    </span>
                  )}
                </>
              )}
            </button>

            {result && (
              <>
                {/* Confirmar en StelOrder */}
                <button
                  onClick={handleConfirm}
                  disabled={confirming || loading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {confirming ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                      Confirmando...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                      </svg>
                      Confirmar en StelOrder
                    </>
                  )}
                </button>

                {/* Split print */}
                <div className="relative">
                  <div className="flex rounded-lg overflow-hidden border border-red-600">
                    <button onClick={() => doPrint(null)} disabled={generandoNotas}
                      className="flex items-center gap-2 px-4 py-2 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-50 transition-colors">
                      {generandoNotas ? (
                        <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
                        </svg>
                      )}
                      {generandoNotas ? `Preparando… ${notasProgress.done}/${notasProgress.total}` : 'Imprimir rutas'}
                    </button>
                    <button onClick={() => setPrintMenu(v => !v)} disabled={generandoNotas}
                      className="px-2 py-2 text-red-600 hover:bg-red-50 disabled:opacity-50 border-l border-red-600 transition-colors" title="Elegir técnico">
                      <svg className={`w-4 h-4 transition-transform ${printMenu?'rotate-180':''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                      </svg>
                    </button>
                  </div>
                  {printMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setPrintMenu(false)}/>
                      <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden min-w-[180px]">
                        <button onClick={() => { doPrint(null); setPrintMenu(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                          </svg>
                          Todo el equipo
                        </button>
                        {allTechnicians.map(nombre => (
                          <button key={nombre} onClick={() => { doPrint(nombre); setPrintMenu(false); }}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: TECH_MAP_COLORS[nombre]||'#6b7280' }}/>
                            {nombre}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Resumen operativo (Parte 1 del plan de notas IA) */}
                <button onClick={doPrintResumenOperativo} disabled={generandoNotas}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-400 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m3 10V11m3 6V9m-9 8h12a2 2 0 002-2V5a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  Resumen operativo
                </button>

                {/* Ver mapa */}
                <button onClick={() => setShowMap(v => !v)}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-semibold transition-colors ${
                    showMap?'bg-gray-800 border-gray-800 text-white':'border-gray-400 text-gray-600 hover:bg-gray-50'
                  }`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                  </svg>
                  {showMap?'Ocultar mapa':'Ver mapa'}
                </button>

              </>
            )}
          </div>

          {/* Leyenda */}
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-3 bg-red-50 border-l-2 border-red-400 rounded-sm inline-block"/>
              Prioridad alta — Pendiente / Ausente
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-3 bg-green-50 border-l-2 border-green-400 rounded-sm inline-block"/>
              Prioridad normal — Gestionada
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-3 bg-blue-50 border-l-2 border-blue-400 rounded-sm inline-block"/>
              Ya asignada en StelOrder
            </span>
            {hasRoutes && (
              <span className="flex items-center gap-1.5 text-gray-400">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <circle cx="7" cy="5" r="1.4"/><circle cx="13" cy="5" r="1.4"/>
                  <circle cx="7" cy="10" r="1.4"/><circle cx="13" cy="10" r="1.4"/>
                  <circle cx="7" cy="15" r="1.4"/><circle cx="13" cy="15" r="1.4"/>
                </svg>
                Arrastra · ⋮ Acciones
              </span>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              {error}
            </div>
          )}

          {/* Warning capacidad al mover orden */}
          {capacityWarning && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800">
              <svg className="w-4 h-4 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              {capacityWarning}
            </div>
          )}

          {/* Alerta técnicos no disponibles */}
          {result && (result.tecnicosNoDisponibles || []).length > 0 && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div>
                <span className="font-semibold">Técnicos no disponibles ese día:</span>{' '}
                {result.tecnicosNoDisponibles.join(', ')} — ausencia o festivo registrado.
              </div>
            </div>
          )}

          {/* Alerta órdenes urgentes &gt;30 días */}
          {result && (result.alertasUrgencia || []).length > 0 && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-4 py-3 text-sm text-red-800">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20A10 10 0 0012 2z"/>
              </svg>
              <div>
                <span className="font-semibold">{result.alertasUrgencia.length} orden{result.alertasUrgencia.length > 1 ? 'es' : ''} con más de 30 días de espera:</span>{' '}
                {result.alertasUrgencia.map(a => a.reference).join(', ')}
              </div>
            </div>
          )}

          {/* Posibles "zombie" (&gt;60 días esperando esta visita, pedido
              2026-10-08): ya no compiten por un hueco, pero se avisa para
              que se revisen y se cierren en StelOrder — nunca desaparecen
              en silencio. */}
          {result && (result.excluidosPorAntiguedad || []).length > 0 && (
            <div className="flex items-start gap-2 bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-700">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div>
                <span className="font-semibold">{result.excluidosPorAntiguedad.length} orden{result.excluidosPorAntiguedad.length > 1 ? 'es' : ''} con más de 60 días esperando (posible zombie, no se ha asignado):</span>{' '}
                {result.excluidosPorAntiguedad.map(a => a.reference).join(', ')} — revisar y cerrar en StelOrder si ya no procede.
              </div>
            </div>
          )}

          {/* Resultado confirmación */}
          {confirmResult && (
            <div className="border border-green-200 rounded-xl overflow-hidden">
              <div className="bg-green-50 px-4 py-2.5 flex items-center gap-2 border-b border-green-200">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span className="text-sm font-semibold text-green-800">
                  Rutas confirmadas — {confirmResult.totalStelCalls} llamadas StelOrder usadas
                </span>
              </div>
              <div className="divide-y divide-green-50 bg-white">
                {Object.entries(confirmResult.resultados || {}).map(([tecnico, r]) => (
                  <div key={tecnico} className="flex items-center gap-3 px-4 py-2.5 flex-wrap">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: TECH_MAP_COLORS[tecnico] || '#9ca3af' }}/>
                    <span className="font-semibold text-sm text-gray-800">{tecnico}</span>
                    {r.yaConfirmada
                      ? <span className="text-xs text-blue-600 font-medium">{r.mensaje}</span>
                      : r.ok
                      ? <span className="text-xs text-green-600 font-medium">{r.nuevasAsignadas} asignadas en StelOrder · secuencia guardada</span>
                      : <span className="text-xs text-red-600 font-medium">{r.error || `${r.errores?.length} errores`}</span>
                    }
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resumen */}
          {result && (
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="bg-gray-100 rounded-full px-3 py-1 text-gray-700 capitalize">
                {result.diaNombre} {new Date(result.fecha+'T12:00:00').toLocaleDateString('es-ES',{day:'2-digit',month:'long'})}
              </span>
              <span className="bg-red-50 text-red-700 rounded-full px-3 py-1">
                <strong>{result.debug?.wosPendientes||0}</strong> pendientes/ausente
              </span>
              <span className="bg-green-50 text-green-700 rounded-full px-3 py-1">
                <strong>{result.debug?.incGestionadas||0}</strong> gestionadas
              </span>
              <span className="bg-blue-50 text-blue-700 rounded-full px-3 py-1">
                <strong>{result.debug?.existentesEnFecha||0}</strong> ya asignadas
              </span>
              <span className="bg-gray-100 rounded-full px-3 py-1 text-gray-700">
                <strong>{result.asignadas}</strong>/{result.totalOrdenes} nuevas asignadas
              </span>
              {availablePool.length > 0 && (() => {
                const byTech = {};
                availablePool.forEach(o => {
                  const t = getTitular(o.zona, dia) || '?';
                  byTech[t] = (byTech[t] || 0) + 1;
                });
                return (
                  <span className="bg-amber-50 text-amber-700 rounded-full px-3 py-1 flex items-center gap-1.5 flex-wrap">
                    <strong>{availablePool.length}</strong> en cola —
                    {Object.entries(byTech).sort((a,b) => b[1]-a[1]).map(([t, n]) => {
                      const tc = TECH_COLORS[t];
                      return (
                        <span key={t} className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded ${tc ? tc.badge : 'bg-gray-100 text-gray-600'}`}>
                          {t} <span className="opacity-70">{n}</span>
                        </span>
                      );
                    })}
                  </span>
                );
              })()}
              {unassignedTotal > 0 && (
                <button onClick={() => setShowUnassigned(v => !v)}
                  className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1 hover:bg-amber-100 transition-colors">
                  ⚠ {sinZonaCount} sin zona
                </button>
              )}
              {/* No caben en la ventana 9:45-17:30 (ver recortarPorVentana()
                  en generate-routes.js, regla de negocio 2026-10-05) — antes
                  estas órdenes desaparecían sin más explicación. */}
              {excluidasPorVentanaCount > 0 && (
                <button onClick={() => setShowExcluidas(v => !v)}
                  className="bg-red-50 text-red-700 border border-red-200 rounded-full px-3 py-1 hover:bg-red-100 transition-colors">
                  ⏰ {excluidasPorVentanaCount} fuera de ventana horaria
                </button>
              )}
            </div>
          )}

          {/* No caben en la ventana horaria (recortadas del final de la ruta
              de su técnico por superar las 17:30) */}
          {result && showExcluidas && excluidasPorVentanaCount > 0 && (
            <div className="border border-red-200 rounded-xl overflow-hidden">
              <div className="bg-red-50 px-4 py-2.5 flex items-center gap-2">
                <span className="text-sm font-semibold text-red-800">No caben en la ventana horaria — {excluidasPorVentanaCount} órdenes</span>
                <span className="text-xs text-red-600">(la ruta de su técnico ya termina a las 17:30)</span>
              </div>
              <div className="divide-y divide-red-50">
                {result.excluidasPorVentana.map(item => (
                  <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 bg-white flex-wrap">
                    <span className="text-xs text-gray-400 font-mono">{item.reference}</span>
                    <span className="text-sm font-medium text-gray-700 flex-1 truncate min-w-0">{item.customerName}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-medium">{item.tecnico}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sin asignar (sin zona) */}
          {result && showUnassigned && sinZonaCount > 0 && (
            <div className="border border-amber-200 rounded-xl overflow-hidden">
              <div className="bg-amber-50 px-4 py-2.5 flex items-center gap-2">
                <span className="text-sm font-semibold text-amber-800">Sin zona reconocida — {sinZonaCount} órdenes</span>
                <span className="text-xs text-amber-600">(CP no incluido en el mapa de zonas, o demasiado lejos de una zona cubierta)</span>
              </div>
              <div className="divide-y divide-amber-50">
                {(result.sinZona||[]).map(item => (
                  <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 bg-white flex-wrap">
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${TIPO_STYLES[item.tipoLabel]||'bg-gray-100 text-gray-600'}`}>
                      {item.tipoLabel}
                    </span>
                    <span className="text-xs text-gray-400 font-mono">{item.reference}</span>
                    <span className="text-sm font-medium text-gray-700 flex-1 truncate min-w-0">{item.customerName}</span>
                    {item.cp
                      ? <span className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">{item.cp}</span>
                      : <span className="text-xs text-amber-600">Sin CP</span>}
                    {item.motivo && <span className="text-xs text-amber-600 w-full">{item.motivo}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Panel "Consultar cola" (pedido 2026-10-08): la cola real del
              backlog tal como la usa el motor, con el nivel y el motivo de
              cada una, y en qué quedó. ── */}
          {showCola && (
            <div className="border border-indigo-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-indigo-50 border-b border-indigo-200">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7"/>
                  </svg>
                  <span className="text-sm font-bold text-indigo-800">Cola completa del backlog</span>
                  <span className="text-xs text-indigo-600">— {colaTotal} órdenes en total, por nivel de prioridad real</span>
                </div>
                <button onClick={() => setShowCola(false)} className="text-indigo-400 hover:text-indigo-700">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <div className="divide-y divide-indigo-50 max-h-[32rem] overflow-y-auto">
                {cola.length === 0 && (
                  <div className="px-4 py-6 text-sm text-gray-400 text-center">Sin órdenes en el backlog</div>
                )}
                {cola.map(item => (
                  <div key={item.id} className="flex items-start gap-3 px-4 py-2.5 bg-white">
                    <span className="text-xs font-mono text-gray-400 w-6 text-right flex-shrink-0 pt-0.5">{item.posicion}</span>
                    <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 flex-shrink-0 ${NIVEL_BADGE[item.nivel] || 'bg-gray-100 text-gray-600'}`}>
                      N{item.nivel}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800 truncate">{item.customerName}</span>
                        <span className="text-xs text-gray-400 font-mono">{item.reference}</span>
                        <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{item.tipoLabel}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{item.motivoNivel}</div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <span className="text-xs text-gray-400">
                        {item.diasEspera != null ? `${item.diasEspera}d esperando` : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {colaTotalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-2 bg-indigo-50 border-t border-indigo-200">
                  <button
                    onClick={() => handleLoadCola(colaPage - 1)}
                    disabled={colaPage <= 1 || colaLoading}
                    className="text-xs font-semibold text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed hover:underline"
                  >
                    ← Anterior
                  </button>
                  <span className="text-xs text-indigo-600">Página {colaPage} de {colaTotalPages}</span>
                  <button
                    onClick={() => handleLoadCola(colaPage + 1)}
                    disabled={colaPage >= colaTotalPages || colaLoading}
                    className="text-xs font-semibold text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed hover:underline"
                  >
                    Siguiente →
                  </button>
                </div>
              )}
              {colaZombies.total > 0 && (
                <div className="border-t border-gray-200">
                  <button
                    onClick={() => setShowZombies(v => !v)}
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 text-xs font-semibold text-gray-600"
                  >
                    <span>⚠ {colaZombies.total} posible{colaZombies.total > 1 ? 's' : ''} zombie — más de 60 días esperando, fuera de la cola</span>
                    <span>{showZombies ? '▲' : '▼'}</span>
                  </button>
                  {showZombies && (
                    <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                      {colaZombies.items.map(z => (
                        <div key={z.id} className="px-4 py-2 bg-white text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-700">{z.customerName}</span>
                            <span className="text-gray-400 font-mono">{z.reference}</span>
                            <span className="text-gray-400">{z.diasEspera}d esperando</span>
                          </div>
                          <div className="text-gray-500 mt-0.5">{z.motivoNivel}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Panel búsqueda de orden para técnico ── */}
          {showOrderSearch && (() => {
            const inZone  = searchTech ? availablePool.filter(o =>  tecnicoMatchesZona(searchTech, o.zona, dia, benitoZonasActivas)) : [];
            const outZone = searchTech ? availablePool.filter(o => !tecnicoMatchesZona(searchTech, o.zona, dia, benitoZonasActivas)) : [];
            const ordersToShow = [...inZone, ...outZone];

            return (
              <div className="border border-amber-200 rounded-xl overflow-hidden">
                {/* Cabecera */}
                <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b border-amber-200">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"/>
                    </svg>
                    <span className="text-sm font-bold text-amber-800">Buscar orden para técnico</span>
                    <span className="text-xs text-amber-600">— {availablePool.length} en cola · misma lógica de zona que el generador</span>
                  </div>
                  <button onClick={() => setShowOrderSearch(false)}
                    className="text-amber-500 hover:text-amber-800 text-lg leading-none font-bold transition-colors">✕</button>
                </div>

                <div className="p-4 space-y-4 bg-white">
                  {/* Selector de técnico */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Técnico:</span>
                    {allTechnicians.map(t => {
                      const tc = TECH_COLORS[t] || { badge: 'bg-gray-100 text-gray-700 border-gray-200' };
                      const inZoneCount = availablePool.filter(o => tecnicoMatchesZona(t, o.zona, dia)).length;
                      return (
                        <button key={t}
                          onClick={() => setSearchTech(prev => prev === t ? '' : t)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                            searchTech === t ? tc.badge : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {t}
                          {inZoneCount > 0 && (
                            <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none ${
                              searchTech === t ? 'bg-white/40' : 'bg-amber-100 text-amber-700'
                            }`}>{inZoneCount}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Lista de órdenes */}
                  {!searchTech && (
                    <p className="text-sm text-gray-400 text-center py-4">Selecciona un técnico para ver las órdenes disponibles</p>
                  )}

                  {searchTech && availablePool.length === 0 && (
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-500">
                      <svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      No quedan órdenes en el pool de espera
                    </div>
                  )}

                  {searchTech && availablePool.length > 0 && (
                    <div className="space-y-2">
                      {inZone.length === 0 && (
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          Sin órdenes en la zona de {searchTech} para este día — se muestran otras zonas
                        </div>
                      )}
                      {inZone.length > 0 && outZone.length > 0 && (
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-1">
                          Zona de {searchTech} ({inZone.length})
                        </div>
                      )}

                      {ordersToShow.map((order, idx) => {
                        const isInZone = tecnicoMatchesZona(searchTech, order.zona, dia);
                        const showSeparator = idx === inZone.length && inZone.length > 0 && outZone.length > 0;
                        return (
                          <React.Fragment key={order.id}>
                            {showSeparator && (
                              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2 pb-1 border-t border-gray-100 mt-2">
                                Otras zonas ({outZone.length})
                              </div>
                            )}
                            <div className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                              isInZone
                                ? 'border-green-200 bg-green-50/30 hover:bg-green-50'
                                : 'border-gray-200 bg-gray-50/30 hover:bg-gray-50'
                            }`}>
                              {/* Prioridad */}
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                order.prioridad === 1 ? 'bg-red-400' : 'bg-green-400'
                              }`}/>
                              {/* Zona + técnico titular */}
                              {(() => {
                                const titular = getTitular(order.zona, dia);
                                const tc = titular ? TECH_COLORS[titular] : null;
                                return (
                                  <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isInZone ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                      {order.zona || '?'}
                                    </span>
                                    {titular && (
                                      <span className={`text-[9px] font-bold px-1 py-0.5 rounded border ${tc ? tc.badge : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                        {titular}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* Tipo */}
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${TIPO_STYLES[order.tipoLabel]||'bg-gray-100 text-gray-600'}`}>
                                {order.tipoLabel}
                              </span>
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-gray-400 font-mono">{order.reference}</span>
                                  {order.diasEspera != null && (
                                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                      esUrgente(order.diasEspera)        ? 'bg-red-100 text-red-700' :
                                      requiereAtencion(order.diasEspera) ? 'bg-amber-100 text-amber-700' :
                                                                           'text-gray-400'
                                    }`}>
                                      {fmtEspera(order.diasEspera)} espera
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm font-medium text-gray-800 truncate">{order.customerName}</div>
                                {order.address && (
                                  <div className="text-xs text-gray-500 truncate">{order.address}</div>
                                )}
                              </div>
                              {/* CP */}
                              {order.cp && (
                                <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono flex-shrink-0">
                                  {order.cp}
                                </span>
                              )}
                              {/* Botón asignar */}
                              <button
                                onClick={() => {
                                  handleAssignFromSearch(searchTech, order.id);
                                  if (!expanded[searchTech]) {
                                    setExpanded(prev => ({ ...prev, [searchTech]: true }));
                                  }
                                }}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex-shrink-0 ${
                                  isInZone
                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                    : 'bg-gray-600 text-white hover:bg-gray-700'
                                }`}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4"/>
                                </svg>
                                Asignar
                              </button>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Tarjetas de técnicos */}
          {Object.values(routeOrders).map(rt => {
            const poolNext = availablePool.find(o => tecnicoMatchesZona(rt.tecnico, o.zona, dia, benitoZonasActivas)) || null;
            return (
              <TechRouteCard
                key={rt.tecnico}
                rt={rt}
                isOpen={!!expanded[rt.tecnico]}
                onToggle={() => setExpanded(prev => ({ ...prev, [rt.tecnico]: !prev[rt.tecnico] }))}
                onReorder={handleReorder}
                allTechnicians={allTechnicians}
                onRemove={handleRemoveOrder}
                onMove={handleMoveOrder}
                activeMenuId={activeMenuId}
                onToggleMenu={handleToggleMenu}
                poolNext={poolNext}
                onAddFromPool={handleAddFromPool}
              />
            );
          })}

          {/* Quitadas de la ruta */}
          {removedOrders.length > 0 && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-600">Quitadas de la ruta — {removedOrders.length}</span>
                <span className="text-xs text-gray-400">(no se imprimirán)</span>
              </div>
              <div className="divide-y divide-gray-100">
                {removedOrders.map(item => (
                  <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 bg-white flex-wrap">
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${TIPO_STYLES[item.tipoLabel]||'bg-gray-100 text-gray-600'}`}>
                      {item.tipoLabel}
                    </span>
                    <span className="text-xs text-gray-400 font-mono">{item.reference}</span>
                    <span className="text-sm font-medium text-gray-700 flex-1 truncate min-w-0">{item.customerName}</span>
                    {item.cp && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">{item.cp}</span>}
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full inline-block"
                        style={{ backgroundColor: TECH_MAP_COLORS[item.tecnicoOrigen]||'#9ca3af' }}/>
                      {item.tecnicoOrigen}
                    </span>
                    <button onClick={() => handleRestoreOrder(item)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 hover:underline">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                      </svg>
                      Restaurar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mapa */}
          {result && showMap && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700">Mapa de rutas</span>
                  {geocodingProg.total > 0 && geocodingProg.done < geocodingProg.total && (
                    <span className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                      <span className="inline-block w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"/>
                      Geocodificando {geocodingProg.done}/{geocodingProg.total}…
                    </span>
                  )}
                  {geocodingProg.total > 0 && geocodingProg.done === geocodingProg.total && (
                    <span className="text-xs text-green-600 font-medium">✓ {geocodingProg.total} geocodificadas</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-600 inline-block"/>Oficina
                  </span>
                  {Object.entries(TECH_MAP_COLORS).map(([name, color]) => (
                    <span key={name} className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor:color }}/>
                      {name}
                    </span>
                  ))}
                </div>
              </div>
              <RouteMap routes={routeOrders} generadoEn={result.generadoEn} geocodedCoords={geocodedCoords}/>
            </div>
          )}

          {/* Estados vacíos */}
          {result && !hasRoutes && unassignedTotal === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No hay órdenes pendientes, ausentes, gestionadas ni asignadas para esta fecha.
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default RouteGenerator;
